import React from 'react';
import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import type { ServiceRequestStatus } from '../../types/serviceRequestListType';
import type { ServiceRequestImage } from '../../types/viewServiceRequestType';
import { STATUS_CONFIG } from './viewServiceRequestHelpers';
import { getFileIcon, openInNewTab } from './attachmentFile';

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
  testId?: string;
  className?: string;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  children,
  testId,
  className,
}) => (
  <div
    className={`border border-[#DCDDE0] rounded-[8px] p-5 flex flex-col gap-4${
      className ? ` ${className}` : ''
    }`}
    data-testid={testId}
  >
    <p className="text-titleDefault font-medium text-on-surface-variant">
      {title}
    </p>
    {children}
  </div>
);

interface InfoFieldProps {
  label: string;
  value: string;
  testId?: string;
}

export const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  testId,
}) => (
  <div className="flex min-w-0 flex-col gap-2">
    <p
      className="text-bodySmall font-normal text-on-surface-variant"
      data-testid={testId}
    >
      {label}
    </p>
    <p
      className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10"
      data-testid={testId ? `${testId}-value` : undefined}
    >
      {value}
    </p>
  </div>
);

interface SRStatusBadgeProps {
  status: ServiceRequestStatus;
}

export const SRStatusBadge: React.FC<SRStatusBadgeProps> = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) {
    return null;
  }
  return (
    <span
      className="inline-flex items-center gap-2 pl-2 pr-[10px] py-1 rounded-[8px] text-sm font-medium overflow-hidden"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.text,
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.1px',
      }}
      data-testid={`view-sr-status-${status}`}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: cfg.text }}
      />
      <span className="wrap-break-word">{cfg.label}</span>
    </span>
  );
};

interface FileRowProps {
  file: ServiceRequestImage;
  testId: string;
  onDownload: (url: string, filename: string) => void;
}

/** A single downloadable/viewable attachment row (icon + filename + actions). */
export const FileRow: React.FC<FileRowProps> = ({
  file,
  testId,
  onDownload,
}) => (
  <div className="flex items-center justify-between gap-3" data-testid={testId}>
    <div className="flex items-center gap-3 min-w-0">
      <button
        type="button"
        onClick={() => openInNewTab(file.url)}
        className="group relative shrink-0 p-0 border-0 bg-transparent cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
        aria-label={`Otwórz ${file.filename}`}
        data-testid={`${testId}-view`}
      >
        {getFileIcon(file.filename)}
        <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity rounded">
          <EyeIcon className="w-5 h-5 text-white" />
        </span>
      </button>
      <span className="text-sm text-[#181A1B] break-all">{file.filename}</span>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      {/* Tablet (and below) only: explicit view action, since touch devices
          have no hover to reveal the eye overlay on the file thumbnail.
          Hidden on desktop (lg+), where the thumbnail overlay handles view. */}
      <button
        type="button"
        onClick={() => openInNewTab(file.url)}
        className="lg:hidden p-2 rounded-full cursor-pointer hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] text-[#181A1B]"
        aria-label={`Otwórz ${file.filename}`}
        data-testid={`${testId}-view-tablet`}
      >
        <EyeIcon className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => onDownload(file.url, file.filename)}
        className="p-2 rounded-full cursor-pointer hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] text-[#181A1B]"
        aria-label={`Pobierz ${file.filename}`}
        data-testid={`${testId}-download`}
      >
        <ArrowDownTrayIcon className="w-5 h-5" />
      </button>
    </div>
  </div>
);

interface EmptyTabStateProps {
  title: string;
  description: string;
  testId?: string;
}

export const EmptyTabState: React.FC<EmptyTabStateProps> = ({
  title,
  description,
  testId,
}) => (
  <div
    className="flex flex-1 flex-col items-center justify-center text-center min-h-[200px]"
    data-testid={testId}
  >
    <p className="text-bodyLarge font-medium text-[#474A4C]">{title}</p>
    <p className="text-bodyMedium text-on-surface-variant mt-2 max-w-md">
      {description}
    </p>
  </div>
);
